import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Box, Typography, Paper, Table, TableHead, TableRow, TableCell,
  TableBody, TextField, Select, MenuItem, FormControl, InputLabel,
  Button, CircularProgress, Alert, Skeleton,
} from "@mui/material";

import { useApi } from "../utils/api";
import { ENDPOINTS } from "../config/endpoints";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const MONTHS = [
  { value: 1, label: "Ocak" }, { value: 2, label: "Şubat" },
  { value: 3, label: "Mart" }, { value: 4, label: "Nisan" },
  { value: 5, label: "Mayıs" }, { value: 6, label: "Haziran" },
  { value: 7, label: "Temmuz" }, { value: 8, label: "Ağustos" },
  { value: 9, label: "Eylül" }, { value: 10, label: "Ekim" },
  { value: 11, label: "Kasım" }, { value: 12, label: "Aralık" },
];

const getDaysInMonth = (year, month) => new Date(year, month, 0).getDate();

const isWeekend = (year, month, day) => {
  const w = new Date(year, month - 1, day).getDay();
  return w === 0 || w === 6;
};

const getWeeklyExceedingHours = (year, month, personHours) => {
  const weeks = {};
  Object.entries(personHours).forEach(([dayStr, values]) => {
    const day = Number(dayStr);
    const date = new Date(year, month - 1, day);
    const mon = new Date(date);
    mon.setDate(date.getDate() + (date.getDay() === 0 ? -6 : 1 - date.getDay()));
    const key = mon.toISOString().slice(0, 10);
    weeks[key] = (weeks[key] || 0) + (Number(values?.ders) || 0);
  });
  return Object.values(weeks).reduce((acc, w) => acc + (w > 20 ? w - 20 : 0), 0);
};

// Skeleton satır — veri yüklenirken gösterilir
const SkeletonRows = ({ count, dayCount }) => (
  <>
    {Array.from({ length: count }).map((_, i) => (
      <TableRow key={i}>
        <TableCell sx={{ position: "sticky", left: 0, bgcolor: "#fff" }}>
          <Skeleton width={100} height={16} />
          <Skeleton width={60} height={12} />
        </TableCell>
        {Array.from({ length: dayCount }).map((_, d) => (
          <TableCell key={d} align="center">
            <Skeleton width={26} height={40} variant="rectangular" sx={{ mx: "auto" }} />
          </TableCell>
        ))}
        <TableCell align="center">
          <Skeleton width={40} height={40} />
        </TableCell>
      </TableRow>
    ))}
  </>
);

const Tally = () => {
  const http = useApi();
  const today = new Date();

  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);

  const userData = JSON.parse(localStorage.getItem("user"));
  // SONRA
  const isAdmin = userData?.type !== "kurum";


  // Tüm veri tek state'te — personel + puantaj + izin
  const [employees, setEmployees] = useState([]);
  const [hours, setHours] = useState({});   // { [pid]: { [gun]: {ders,nobet,fazla} } }
  const [izinliGunler, setIzinliGunler] = useState({});   // { [pid]: { [gun]: "R"|"İ" } }

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [kurumlar, setKurumlar] = useState([]);
  const [selectedKurum, setSelectedKurum] = useState(
    isAdmin ? "" : (userData?.id ?? "")  // kurum ise kendi id'si
  );

  // Önceki personel sayısını tut — skeleton için
  const [prevEmpCount, setPrevEmpCount] = useState(3);

  // ─── Kurumları yükle (sadece admin) ───────────────────────────────────────
  useEffect(() => {
    if (!isAdmin) return;
    http.get("/kurumlar")
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        setKurumlar(list);
        if (list.length) setSelectedKurum(list[0].id);
      })
      .catch(console.error);
  }, [http, isAdmin]);

  // ─── TEK İSTEK: personel + puantaj + izin ────────────────────────────────
  useEffect(() => {
    if (!selectedKurum) return;

    setLoading(true);
    setError("");

    // Mevcut personel sayısını skeleton için kaydet
    if (employees.length) setPrevEmpCount(employees.length);

    http.get(ENDPOINTS.PUANTAJ.OZET, { yil: year, ay: month, kurum_id: selectedKurum })
      .then((res) => {
        if (!res) return;

        // Personel — alfabetik (Türkçe locale, ad+soyad)
        setEmployees(
          Array.isArray(res.personeller)
            ? res.personeller
                .filter((p) => p.aktif !== false)
                .sort((a, b) =>
                  `${a.ad ?? ""} ${a.soyad ?? ""}`.localeCompare(
                    `${b.ad ?? ""} ${b.soyad ?? ""}`,
                    "tr",
                    { sensitivity: "base" }
                  )
                )
            : []
        );

        // Puantaj: backend { pid: { gun: {ders,nobet,fazla} } } döndürüyor
        setHours(res.puantaj || {});

        // İzinler: backend { pid: { gun: "R"|"İ" } } döndürüyor
        setIzinliGunler(res.izinler || {});
      })
      .catch((err) => setError(err.message || "Veriler alınamadı"))
      .finally(() => setLoading(false));
  }, [http, selectedKurum, year, month]);

  const daysArray = useMemo(() => {
    const count = getDaysInMonth(year, month);
    return Array.from({ length: count }, (_, i) => i + 1);
  }, [year, month]);

  const yearOptions = useMemo(() => {
    const cur = today.getFullYear();
    return [cur - 1, cur, cur + 1];
  }, []);

  const handleChangeHour = useCallback((personelId, day, type, value) => {
    const numeric = value === "" ? "" : Number(value);
    if (numeric !== "" && (Number.isNaN(numeric) || numeric < 0)) return;
    setHours((prev) => ({
      ...prev,
      [personelId]: {
        ...(prev[personelId] || {}),
        [day]: {
          ...(prev[personelId]?.[day] || {}),
          [type]: value === "" ? "" : numeric,
        },
      },
    }));
  }, []);

  // ─── Kaydet ───────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    try {
      await http.post(ENDPOINTS.PUANTAJ.KAYDET, { yil: year, ay: month, data: hours });
      alert("Puantaj başarıyla kaydedildi!");
    } catch (err) {
      alert(err.message || "Puantaj kaydedilemedi.");
    } finally {
      setSaving(false);
    }
  };

  // ─── Excel Export (backend) ───────────────────────────────────────────────
  const handleExportExcel = async () => {
    if (!selectedKurum) return;
    try {
      const blob = await http.getBlob(
        `${ENDPOINTS.PUANTAJ.EXCEL}?yil=${year}&ay=${month}&kurum_id=${selectedKurum}`
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `puantaj_${year}_${String(month).padStart(2, "0")}.xlsx`;
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || "Excel indirilemedi");
    }
  };

  // ─── Excel Import (backend) ───────────────────────────────────────────────
  const handleImportExcel = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedKurum) {
      e.target.value = "";
      return;
    }
    setSaving(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await http.postForm(
        `${ENDPOINTS.PUANTAJ.EXCEL}?yil=${year}&ay=${month}&kurum_id=${selectedKurum}`,
        fd
      );
      alert(
        `Yükleme tamam: ${res?.personel_basarili ?? 0} personel kaydedildi` +
          (res?.personel_atlandi ? ` • ${res.personel_atlandi} atlandı` : "") +
          (res?.hatalar?.length ? ` • ${res.hatalar.length} hatalı satır` : "")
      );
      // Yeniden çek
      const fresh = await http.get(ENDPOINTS.PUANTAJ.OZET, {
        yil: year, ay: month, kurum_id: selectedKurum,
      });
      setHours(fresh.puantaj || {});
    } catch (err) {
      setError(err.message || "Excel yüklenemedi");
    } finally {
      setSaving(false);
      e.target.value = "";
    }
  };

  // ─── PDF Export ───────────────────────────────────────────────────────────
  const handleExportPDF = () => {
    if (!employees.length) return;
    const doc = new jsPDF("landscape", "pt", "a4");
    const monthLabel = MONTHS.find((m) => m.value === month)?.label || "";
    doc.setFontSize(12);
    doc.text(`Puantaj Raporu - ${monthLabel} ${year}`, 40, 30);
    const header = ["Personel", ...daysArray.map(String), "D", "N", "F"];
    const body = employees.map((emp) => {
      const ph = hours[emp.id] || {};
      const row = [`${emp.ad || ""} ${emp.soyad || ""}`.trim() || `#${emp.id}`];
      let td = 0, tn = 0, tf = 0;
      daysArray.forEach((day) => {
        const v = ph[day];
        if (v) {
          const d = Number(v.ders) || 0, n = Number(v.nobet) || 0, f = Number(v.fazla) || 0;
          const parts = [];
          if (d) parts.push(`D${d}`); if (n) parts.push(`N${n}`); if (f) parts.push(`F${f}`);
          row.push(parts.join(" ") || "");
          td += d; tn += n; tf += f;
        } else row.push("");
      });
      row.push(td || "", tn || "", tf || "");
      return row;
    });
    autoTable(doc, {
      head: [header], body, startY: 40,
      styles: { fontSize: 6, cellPadding: 2 },
      headStyles: { fillColor: [245, 245, 245], textColor: 20, lineWidth: 0.1 },
      bodyStyles: { lineWidth: 0.1 },
      columnStyles: { 0: { cellWidth: 80 } },
      margin: { left: 20, right: 20 },
    });
    doc.save(`puantaj_${year}_${String(month).padStart(2, "0")}.pdf`);
  };

  return (
    <Box>
      {/* Üst bar */}
      {/* D / N / F Açıklama */}
      <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
        {[
          { kisa: "D", tam: "Ders Saati", renk: "#2563EB", bg: "#EFF6FF" },
          { kisa: "N", tam: "Nöbet Saati", renk: "#7C3AED", bg: "#F5F3FF" },
          { kisa: "F", tam: "Fazla Mesai", renk: "#D97706", bg: "#FFFBEB" },
          { kisa: "R", tam: "Rapor (İzin)", renk: "#991B1B", bg: "#FEF2F2" },
          { kisa: "İ", tam: "İzinli Gün", renk: "#1E40AF", bg: "#EFF6FF" },
        ].map((item) => (
          <Box key={item.kisa} sx={{ display: "flex", alignItems: "center", gap: 0.8, bgcolor: item.bg, px: 1.5, py: 0.6, borderRadius: 2 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: item.renk, minWidth: 14 }}>
              {item.kisa}
            </Typography>
            <Typography sx={{ fontSize: 12, color: item.renk }}>
              {item.tam}
            </Typography>
          </Box>
        ))}
      </Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: "bold" }}>Puantaj / Mesai Takip</Typography>
          <Typography variant="body1" color="text.secondary">
            Personellerin aylık çalışma saatlerini girin.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Ay</InputLabel>
            <Select label="Ay" value={month} onChange={(e) => setMonth(Number(e.target.value))}>
              {MONTHS.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 90 }}>
            <InputLabel>Yıl</InputLabel>
            <Select label="Yıl" value={year} onChange={(e) => setYear(Number(e.target.value))}>
              {yearOptions.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
            </Select>
          </FormControl>
          <Button variant="outlined" size="small" onClick={handleExportExcel}
            disabled={loading || !selectedKurum}>Excel İndir</Button>
          <Button
            variant="outlined"
            size="small"
            component="label"
            disabled={loading || saving || !selectedKurum}
          >
            Excel'den Yükle
            <input
              type="file"
              accept=".xlsx"
              hidden
              onChange={handleImportExcel}
            />
          </Button>
          <Button variant="outlined" size="small" onClick={handleExportPDF}
            disabled={loading || !employees.length}>PDF İndir</Button>
          <Button variant="contained" color="primary" size="small" onClick={handleSave}
            disabled={saving || loading || !employees.length}>
            {saving ? <CircularProgress size={16} color="inherit" /> : "Kaydet"}
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {isAdmin && (
        <FormControl size="small" sx={{ mb: 2, minWidth: 200 }}>
          <InputLabel>Kurum Seç</InputLabel>
          <Select value={selectedKurum} label="Kurum Seç"
            onChange={(e) => setSelectedKurum(e.target.value)}>
            {kurumlar.map((k) => <MenuItem key={k.id} value={k.id}>{k.kurum_adi}</MenuItem>)}
          </Select>
        </FormControl>
      )}

      {!selectedKurum ? (
        <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
          Lütfen kurum seçin.
        </Typography>
      ) : (
        <Paper sx={{ width: "100%", overflowX: "auto", mt: 1 }}>
          <Table size="small" stickyHeader sx={{
            minWidth: 650,
            "& th": { whiteSpace: "nowrap", padding: "3px 4px", fontSize: 10 },
            "& td": { padding: "2px 3px", fontSize: 9 },
          }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{
                  position: "sticky", left: 0, zIndex: 2,
                  bgcolor: "#f5f5f5", minWidth: 120, fontWeight: 600,
                }}>Personel</TableCell>
                {daysArray.map((day) => {
                  const weekend = isWeekend(year, month, day);
                  const isToday = today.getDate() === day
                    && today.getMonth() + 1 === month
                    && today.getFullYear() === year;
                  return (
                    <TableCell key={day} align="center" sx={{
                      minWidth: 30, px: 0.5,
                      bgcolor: weekend ? "#F3F4F6" : isToday ? "#DBEAFE" : "#FAFAFA",
                      color: weekend ? "#9CA3AF" : "inherit",
                      borderLeft: "1px solid #E5E7EB", fontSize: 9,
                    }}>{day}</TableCell>
                  );
                })}
                <TableCell align="center" sx={{ minWidth: 60, fontWeight: 600, bgcolor: "#f5f5f5" }}>
                  Toplam
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {/* Yüklenirken skeleton göster — boş ekran yerine */}
              {loading ? (
                <SkeletonRows count={prevEmpCount} dayCount={daysArray.length} />
              ) : !employees.length ? (
                <TableRow>
                  <TableCell colSpan={daysArray.length + 2} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">Bu kuruma ait personel bulunamadı.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                employees.map((emp) => {
                  const personHours = hours[emp.id] || {};
                  const dersExceed = getWeeklyExceedingHours(year, month, personHours);
                  const totals = daysArray.reduce((acc, day) => {
                    const g = personHours[day] || {};
                    acc.nobet += Number(g.nobet) || 0;
                    acc.fazla += Number(g.fazla) || 0;
                    return acc;
                  }, { nobet: 0, fazla: 0 });

                  return (
                    <TableRow key={emp.id} hover>
                      <TableCell sx={{
                        position: "sticky", left: 0, zIndex: 1, bgcolor: "#FFFFFF", minWidth: 120,
                      }}>
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                          <Typography variant="body2" fontWeight={600} sx={{ fontSize: 11 }}>
                            {emp.ad} {emp.soyad}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 9 }}>
                            {emp.istihdam_turu
                              ? emp.istihdam_turu.split("_")
                                .map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ")
                              : ""}
                          </Typography>
                        </Box>
                      </TableCell>

                      {daysArray.map((day) => {
                        const izinKodu = izinliGunler?.[emp.id]?.[day];
                        const isIzinli = Boolean(izinKodu);
                        const weekend = isWeekend(year, month, day);
                        return (
                          <TableCell key={day} align="center"
                            sx={{ bgcolor: weekend ? "#F3F4F6" : "white", px: 0.5 }}>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3 }}>
                              {isIzinli && (
                                <Typography sx={{
                                  fontSize: 11, fontWeight: "bold", textAlign: "center", lineHeight: 1,
                                  color: izinKodu === "R" ? "#991B1B" : "#1E40AF",
                                }}>{izinKodu}</Typography>
                              )}
                              {["ders", "nobet", "fazla"].map((type) => (
                                <TextField
                                  key={type}
                                  variant="outlined" size="small" type="number"
                                  disabled={isIzinli}
                                  value={personHours?.[day]?.[type] ?? ""}
                                  onChange={(e) => handleChangeHour(emp.id, day, type, e.target.value)}
                                  placeholder={type === "ders" ? "D" : type === "nobet" ? "N" : "F"}
                                  inputProps={{
                                    min: 0, step: 0.5,
                                    style: { textAlign: "center", padding: "1px 2px", fontSize: 8, width: 26 },
                                  }}
                                  sx={{
                                    "& input[type=number]::-webkit-inner-spin-button": {
                                      WebkitAppearance: "none",
                                    },
                                  }}
                                />
                              ))}
                            </Box>
                          </TableCell>
                        );
                      })}

                      <TableCell align="center" sx={{ fontSize: 9, bgcolor: "#F9FAFB", minWidth: 60 }}>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3 }}>
                          <Typography sx={{ fontSize: 9 }}>D: {dersExceed > 0 ? Math.floor(dersExceed) : "-"}</Typography>
                          <Typography sx={{ fontSize: 9 }}>N: {totals.nobet > 0 ? totals.nobet : "-"}</Typography>
                          <Typography sx={{ fontSize: 9 }}>F: {totals.fazla > 0 ? totals.fazla : "-"}</Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
};

export default Tally;